import * as api from "@/api";
import LocalWallpaper from "@/classes/local-wallpaper";
import { changeEditorFontSize, setValue } from "@/code-editor";
import { configExists, getFolderName, openFile } from "@/file";
import globals from "@/globals";
import { saveSettings } from "@/settings";
import * as signals from "@/signals";
import { deleteBackup, getDomain, getSuffix, loadSites, newSite } from "@/site";
import {
	Column,
	ContextMenuInfo,
	DialogInfo,
	MenuItemInfo,
	PromptInfo,
	RealIdLevel,
	SiteContent,
} from "@/types";
import i18n, { t } from "i18next";
import { MouseEvent } from "react";

export async function closeAiBar(): Promise<void> {
	if (!signals.isAiBarShown.value) {
		return;
	}
	signals.isClosingSidebar.value = true;
	document.documentElement.classList.remove("has-sidebar");
	await sleep(globals.ANIMATION_WAIT_TIME);
	signals.isAiBarShown.value = false;
	signals.isClosingSidebar.value = false;
}

export async function closeDialog(): Promise<void> {
	if (!signals.dialog.value.text) {
		return;
	}
	globals.dialogCallbacks = {};
	signals.isClosingDialog.value = true;
	await sleep(globals.ANIMATION_WAIT_TIME);
	signals.dialog.value = {};
	signals.isClosingDialog.value = false;
}

export function closePopup(key: string): void {
	signals.arePopupsOpen.value = {
		...signals.arePopupsOpen.value,
		[key]: false,
	};
	for (const value of Object.values(signals.arePopupsOpen.value)) {
		if (value) {
			return;
		}
	}
}

export async function closeSidebars(): Promise<void> {
	await closeAiBar();
	await closeUploadCenter();
}

export async function closeUploadCenter(): Promise<void> {
	if (signals.pendingUploadFiles.value.length === 0) {
		return;
	}
	signals.isClosingSidebar.value = true;
	await sleep(globals.ANIMATION_WAIT_TIME);
	signals.pendingUploadFiles.value = [];
	globals.pendingUploadFiles = {};
	signals.isClosingSidebar.value = false;
}

export async function copyText(text: string): Promise<void> {
	await navigator.clipboard.writeText(text);
	void showToast(t("copiedToClipboard"));
}

export function encodeData(
	data: Record<string, string | null | undefined>,
): string {
	const array: string[] = [];
	for (const key in data) {
		const value = data[key];
		if (!value) {
			continue;
		}
		array.push(key + "=" + encodeURIComponent(value));
	}
	return array.join("&");
}

export function downloadDataUrl(dataUrl: string, filename: string): void {
	const newLink = document.createElement("a");
	newLink.hidden = true;
	newLink.href = dataUrl;
	newLink.download = filename;
	document.body.appendChild(newLink);
	newLink.click();
	newLink.remove();
}

export function downloadText(text: string, filename: string): void {
	downloadDataUrl(
		"data:text/plain;charset=utf-8," + encodeURIComponent(text),
		filename,
	);
}

export function getRthCdnBaseUrl(): string {
	return isMainland()
		? "https://cdn.rthe.cn/"
		: "https://cdn.rthsoftware.cn/";
}

export function getSupportLink(): string {
	return (
		"https://support.retiehe.com/?" +
		encodeData({
			app: globals.APP_NAME,
			username: signals.login.value.username,
		})
	);
}

export function handleKeyboardClick(
	onClick: () => void,
): (event: KeyboardEvent | React.KeyboardEvent<Element>) => void {
	return (event: KeyboardEvent | React.KeyboardEvent<Element>): void => {
		switch (event.key) {
			case "Enter":
			case " ": {
				onClick();
				event.preventDefault();
				break;
			}
			default:
				break;
		}
	};
}

export function finishLoading(): void {
	if (
		signals.dynamicInfo.value.isLoaded &&
		globals.loadCount >=
			(signals.login.value.username ? globals.expectedLoadCount : 1)
	) {
		signals.isSplashScreenShown.value = false;
		if (globals.hasRealId && signals.sites.value.length === 0) {
			newSite();
		}
	}
}

export async function getPremium(): Promise<void> {
	const handleVisibilityChange = (): void => {
		if (!document.hidden) {
			void loadExpirationTime();
			removeEventListener("visibilitychange", handleVisibilityChange);
		}
	};

	window.open(
		"https://account.retiehe.com/premium?" +
			encodeData({
				lang: i18n.language,
				service: "rthe",
				test: process.env.NODE_ENV === "development" ? "1" : undefined,
			}),
	);
	if (signals.login.value.username) {
		addEventListener("visibilitychange", handleVisibilityChange);
		await sleep(globals.ANIMATION_WAIT_TIME);
		await showDialog(t("refreshIfPaid"), {
			showCancel: true,
		});
		window.location.reload();
	}
}

export function isMainland(): boolean {
	if (!signals.dynamicInfo.value.region) {
		const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
		return timeZone === "Asia/Shanghai";
	}
	return signals.dynamicInfo.value.region === "CN";
}

export function isPremium(): boolean {
	if (globals.privilege) {
		return true;
	} else {
		showDialog(t("premiumOnly") + t("refreshIfJustPaid"), {
			showCancel: true,
		})
			.then(getPremium)
			.catch(console.error);
		return false;
	}
}

export async function loadExpirationTime(): Promise<void> {
	try {
		const data = await api.getExpirationTime();
		signals.premiumRemaining.value = data.time
			? Math.round((data.time - Date.now() / 1000) / 86400)
			: 0;
		globals.privilege = data.encrypted || false;
		signals.hasPremium.value = !!globals.privilege;
		if (
			globals.privilege &&
			!globals.isTencent &&
			!navigator.userAgent.includes("HeyTapBrowser") &&
			!navigator.userAgent.includes("HuaweiBrowser")
		) {
			signals.sites.value = [...signals.sites.value];
			if (signals.currentSite.value) {
				signals.currentSite.value =
					getDomain(signals.currentSite.value) + getSuffix();
			}
		}
		if (
			signals.premiumRemaining.value > 0 &&
			signals.premiumRemaining.value < 3
		) {
			showFloatNotification({
				onClick: () => {
					window.open(
						"https://account.retiehe.com/?" +
							encodeData({
								page: "premium",
								service: "host",
							}),
					);
				},
				text: t("renewBeforeExpiresToGet20PercentOff"),
				title: t("premiumExpiresWithin3Days"),
			});
		}
	} catch (error) {
		void api.handleApiError(error);
		globals.privilege = false;
	}
}

export function loggedIn(isNewLogin: boolean): void {
	if (isNewLogin) {
		signals.isLoginPanelShown.value = false;
	}
	signals.currentColumn.value = Column.SITE_LIST;
	loadSites()
		.then(async () => {
			globals.loadCount++;
			if (signals.sites.value.length === 0) {
				if (
					globals.hasRealId &&
					globals.loadCount >= globals.expectedLoadCount
				) {
					newSite();
				}
			} else {
				const openedHost = localStorage.getItem("CurrentHostname");
				if (openedHost) {
					if (signals.sites.value.includes(getDomain(openedHost))) {
						await showDialog(
							t("confirmRestoreUnsavedData"),
							{
								showCancel: true,
							},
							{
								cancel: deleteBackup,
							},
						);
						signals.currentSite.value = openedHost;
						signals.files.value = JSON.parse(
							localStorage.getItem("CurrentHost") || "{}",
						) as SiteContent;
						globals.currentHostInitial = JSON.parse(
							JSON.stringify(signals.files.value),
						) as SiteContent;
						const siteContent = signals.files.value;
						if (!configExists(siteContent)) {
							return;
						}
						const config = siteContent["settings.rth"];
						signals.currentFile.value = config.home;
						signals.currentFolder.value = getFolderName(
							config.home,
						);
						void openFile(config.home);
					} else {
						deleteBackup();
					}
				}
			}
			finishLoading();
		})
		.catch(console.error);
	loadExpirationTime()
		.then(async () => {
			if (globals.privilege) {
				try {
					const data = await api.getSettings();
					if (
						data.fontSize &&
						globals.settings.fontSize !== data.fontSize
					) {
						globals.settings.fontSize = Number(data.fontSize);
						changeEditorFontSize(false);
					}
					if (data.jsProcessor) {
						globals.settings.jsProcessor = data.jsProcessor;
					}
					saveSettings();
				} catch (error) {
					void api.handleApiError(error);
				}
			} else if (globals.settings.theme === "wallpaper") {
				globals.settings.theme = "dark";
				saveSettings();
				const localWallpaper = LocalWallpaper.getInstance();
				void localWallpaper.deleteWallpaper();
				document.documentElement.classList.remove("has-bg-img");
			}
			globals.loadCount++;
			finishLoading();
		})
		.catch(console.error);
	api.getIntroForLoggedIn()
		.then((data) => {
			if (data && !signals.currentSite.value) {
				setValue(data);
			}
		})
		.catch(api.handleApiError);
	api.getRealId()
		.then((data) => {
			globals.loadCount++;
			globals.hasRealId = data.realIdLevel >= RealIdLevel.PHONE_VERIFIED;
			if (!globals.hasRealId) {
				globals.newSiteOverride = (): void => {
					void showRealIdDialog();
				};
				void showRealIdDialog();
			}
			finishLoading();
		})
		.catch((error) => {
			void api.handleApiError(error);
			globals.newSiteOverride = (): void => {
				void showDialog(t("essentialComponentNotLoaded"));
			};
		});
}

export function openPopup(key: string): void {
	signals.arePopupsOpen.value = {
		...signals.arePopupsOpen.value,
		[key]: true,
	};
}

export function parseJson<T>(str: string): T {
	try {
		return JSON.parse(str) as T;
	} catch (error) {
		console.error(error);
		return {} as T;
	}
}

export function showContextMenu(
	event: MouseEvent | undefined,
	menuItems: {
		content: MenuItemInfo;
		onClick: () => void | Promise<void>;
		when: boolean;
	}[],
): void {
	const newContextMenuInfo: ContextMenuInfo = {
		items: [],
		x: event?.clientX || 0,
		y: event?.clientY || 0,
	};
	globals.contextMenuCallbacks = {};
	for (const item of menuItems) {
		if (!item.when) {
			continue;
		}
		newContextMenuInfo.items.push(item.content);
		globals.contextMenuCallbacks[item.content.text] = item.onClick;
	}
	signals.contextMenuInfo.value = newContextMenuInfo;
}

export function showDialog(
	text: string,
	options: DialogInfo = {},
	callbacks?: typeof globals.dialogCallbacks,
): Promise<void> {
	return new Promise<void>((resolve) => {
		if (callbacks) {
			globals.dialogCallbacks = callbacks;
		}
		if (!globals.dialogCallbacks.ok) {
			globals.dialogCallbacks.ok = (): void => {
				resolve();
			};
		}
		options.text = text;
		signals.dialog.value = options;
	});
}

export function showFloatNotification({
	isFolded,
	onClick,
	onClose,
	text,
	title,
}: {
	isFolded?: boolean;
	onClick?: () => void;
	onClose?: () => void;
	text: string;
	title: string;
}): void {
	globals.floatNotificationCallbacks.onClick = onClick || null;
	globals.floatNotificationCallbacks.onClose = onClose || null;
	signals.floatNotificationInfo.value = {
		isFolded: isFolded || false,
		text: text,
		title: title,
	};
}

export function showPrompt(
	text: string,
	options: PromptInfo = {},
): Promise<string> {
	return new Promise<string>((resolve) => {
		options.text = text;
		signals.prompt.value = options;
		globals.promptCallback = resolve;
	});
}

export async function showRealIdDialog(): Promise<void> {
	await closeDialog();
	await showDialog(t("requirePhoneNumber"), {
		title: t("lastStep").toString(),
	});
	window.location.href =
		"https://account.retiehe.com/?" +
		encodeData({
			action: "changePhone",
			lang: i18n.language,
			test: process.env.NODE_ENV === "development" ? "1" : undefined,
		});
}

export async function showToast(text: string, timeout?: number): Promise<void> {
	if (signals.toast.value?.text) {
		signals.toast.value = {};
		await sleep(1);
	}
	signals.toast.value = {
		text: text,
		timeout: timeout,
	};
}

export function sleep(delay: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, delay);
	});
}
